import { ClientInjuryItem, MedicalListItem, TemplateVariableKeys, TemplateVariableValue } from '../types';

/**
 * Helper to format a TemplateVariableValue as a string for contexts that require string values.
 * Arrays are joined with semicolons, null/undefined become empty string.
 */
const formatValueAsString = (value: TemplateVariableValue): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    // Check if it's a MedicalListItem array or ClientInjuryItem array
    if (value.length > 0 && 'name' in value[0]) {
      return (value as Array<MedicalListItem>)
        .map((item) => `${item.name} - ${item.address}`)
        .join('; ');
    }
    if (value.length > 0 && 'bodyPart' in value[0]) {
      return (value as Array<ClientInjuryItem>)
        .map((item) => `${item.bodyPart}: ${item.description}`)
        .join('; ');
    }
    return '';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return value;
};

/**
 * Returns sample template values as string-only Record.
 * Use this for APIs that expect Record<string, string | undefined>.
 *
 * @deprecated Use getSampleTemplateValuesForRendering() for template preview/rendering
 * which preserves arrays needed for Jinja2 {% for %} loops.
 */
export const getSampleTemplateValuesAsStrings = (): Record<string, string | undefined> => {
  const result: Record<string, string | undefined> = {};
  Object.entries(sampleTemplateValues).forEach(([key, value]) => {
    result[key] = formatValueAsString(value) || undefined;
  });
  return result;
};

/**
 * Returns sample template values preserving arrays and objects for template rendering.
 * Use this for DOCX template preview which needs arrays for Jinja2 {% for %} loops.
 */
export const getSampleTemplateValuesForRendering = (): Record<
  string,
  string | Array<MedicalListItem> | Array<ClientInjuryItem> | Record<string, unknown> | undefined
> => {
  const result: Record<string, string | Array<MedicalListItem> | Array<ClientInjuryItem> | Record<string, unknown> | undefined> = {};
  Object.entries(sampleTemplateValues).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      result[key] = undefined;
    } else if (Array.isArray(value)) {
      // Preserve arrays for Jinja2 iteration (e.g., matterMedicalList, clientInjuries)
      result[key] = value;
    } else {
      result[key] = value;
    }
  });
  return result;
};

export const sampleTemplateValues: Record<TemplateVariableKeys, TemplateVariableValue> = {
  // Firm Information
  firmName: 'Johnson & Associates',
  firmFullName: 'Johnson & Associates Legal Services, LLC',
  firmParalegalSig: 'https://example.com/signatures/paralegal.png',
  firmManagingParalegalName: 'Jennifer Martinez',
  firmAddress1: '100 N Central Ave',
  firmAddress2: 'Suite 2100, Phoenix, AZ 85004',

  // Matter Details
  matterName: 'Smith v. ABC Corporation',
  matterStatus: 'Active',
  matterMatterType: 'Personal Injury',
  matterDateOfLoss: '2023-06-15',
  matterCity: 'Phoenix',
  matterState: 'Arizona',
  matterLocation: 'Phoenix, AZ',
  matterStatuteOfLimitationsDate: '2025-06-15',
  matterAllClientNames: 'Robert Smith and Jane Smith',
  matterMedicalList: [
    { name: 'Phoenix General Hospital', address: '123 Hospital Dr, Phoenix, AZ 85001' },
    { name: 'Valley Physical Therapy', address: '456 Therapy Ave, Scottsdale, AZ 85251' },
    { name: 'Southwest Orthopedics', address: '789 Medical Blvd, Tempe, AZ 85281' },
  ],

  // Attorney Information
  attorneySig: 'https://example.com/signatures/attorney.png',

  // Client Information
  clientName: 'Robert Smith',
  clientFirstName: 'Robert',
  clientLastName: 'Smith',
  clientEmail: 'robert.smith@email.com',
  clientDateOfBirth: '1985-03-22',
  clientAddress: '123 Main Street, Phoenix, AZ 85001',
  clientAddress1: '123 Main Street',
  clientAddress2: 'Apt 4B',
  clientAddress3: '',
  clientCity: 'Phoenix',
  clientState: 'AZ',
  clientZip: '85001',
  clientPhone: '(602) 555-0123',
  clientLastFourOfSsn: '4321',
  clientSsn: '123-45-4321',
  clientHipaaSignedDate: '2023-06-15',
  clientHipaaSigUrl: 'https://example.com/signatures/client-hipaa.png',
  clientHipaaCleanedSigUrl:
    'https://example.com/signatures/client-hipaa-cleaned.png',
  clientInjuries: [
    { bodyPart: 'Cervical Spine', description: 'Whiplash injury with limited range of motion' },
    { bodyPart: 'Lumbar Spine', description: 'Lower back strain and chronic pain' },
    { bodyPart: 'Shoulder (Right)', description: 'Rotator cuff injury' },
  ],

  // Police Information
  policeName: 'NYPD',
  policeAddress: '321 Police Plaza, Phoenix, AZ 85003',
  policePhone: '(602) 555-7890',
  policeEmail: 'jwilson@phoenixpd.gov',
  policePoliceReportNumber: 'PHX-2023-456789',

  // Medical Information
  medicalName: 'Phoenix General Hospital',
  medicalDatesOfService: '2023-06-15 to 2023-07-30',
  medicalTreatment: 'Physical Therapy, Surgery',
  medicalAddress: '789 Hospital Drive, Phoenix, AZ 85008',
  medicalAddress1: '789 Hospital Drive',
  medicalAddress2: 'Phoenix, AZ 85008',
  medicalPhone: '(602) 555-4567',
  medicalEmail: 'records@phoenixgeneral.com',
  medicalStillTreating: 'Yes',
  medicalDateOfRecordsRequest: '2023-06-15',
  // Incident Information
  incidentDescription:
    'Robert Smith was involved in a rear-end collision with another vehicle',
  incidentDateOfLoss: '2023-06-15',
  incidentTimeOfLoss: '14:30',
  incidentLocation: 'Intersection of Main St and 1st Ave, Phoenix, AZ',
  incidentInjuriesDescribed: 'Whiplash, back pain, and shoulder injury',
  incidentIncidentType: 'Motor Vehicle Accident, Rear End',
  policeReportedIncidentDate: '2023-06-15',
  policeDescription: 'Police report submitted',
  policeReportedLocation: 'Intersection of Main St and 1st Ave, Phoenix, AZ',
  policeAttestationStatus: 'Attested',
  incidentHospital: 'Phoenix General Hospital',
  incidentAmbulance: 'Phoenix Fire Department Ambulance Service',

  // Insurance Details
  insuranceDateFiled: '2023-06-20',
  insurancePolicyType: 'Auto Insurance',
  insuranceHolder: 'Robert Smith',
  insuranceCarrier: 'Safe Auto Insurance Co.',
  insurancePolicyNumber: 'POL-987654321',
  insurancePolicyLimitations: '100000/300000',
  insuranceVehicleDescription: '2020 Toyota Camry',
  insuranceClaimNumber: 'CLM-123456789',
  insuranceAdjusterName: 'Sarah Johnson',
  insuranceAdjusterPhone: '(800) 555-9876',
  insuranceAdjusterEmail: 'sjohnson@safeauto.com',
  insuranceMemberId: 'MEM123456',
  insuranceGroupId: 'GRP789012',
  insuranceCarrierPhone: '(800) 555-9800',
  insuranceHealthCarrierType: 'MEDICAID_TRADITIONAL',
  insuranceHealthCarrierProgramName: 'Medicaid',

  // 1P Auto Insurance (First-Party)
  insurance1pCarrier: 'State Farm Insurance',
  insurance1pPolicyNumber: 'SF-1P-456789',
  insurance1pClaimNumber: '1P-CLM-2023-001',
  insurance1pAdjusterName: 'Michael Brown',
  insurance1pAdjusterPhone: '(800) 555-1234',
  insurance1pAdjusterEmail: 'mbrown@statefarm.com',
  insurance1pPolicyLimitations: '50000/100000',

  // 3P Auto Insurance (Third-Party)
  insurance3pCarrier: 'Progressive Insurance',
  insurance3pPolicyNumber: 'PRG-3P-789012',
  insurance3pClaimNumber: '3P-CLM-2023-002',
  insurance3pAdjusterName: 'Lisa Anderson',
  insurance3pAdjusterPhone: '(800) 555-5678',
  insurance3pAdjusterEmail: 'landerson@progressive.com',
  insurance3pPolicyLimitations: '100000/300000',

  // Health Insurance
  insuranceHealthCarrier: 'Blue Cross Blue Shield',
  insuranceHealthPolicyNumber: 'BCBS-H-345678',
  insuranceHealthMemberId: 'BCBS-MEM-123456',
  insuranceHealthGroupId: 'BCBS-GRP-789012',
  insuranceHealthAdjusterName: 'Jennifer Wilson',
  insuranceHealthAdjusterPhone: '(800) 555-9012',
  insuranceHealthAdjusterEmail: 'jwilson@bcbs.com',

  treatmentRequestDate: '2023-06-15',
  treatmentRequestServiceStartDate: '2023-06-15',
  treatmentRequestServiceEndDate: '07/30/2023',
  treatmentChannelType: 'fax',
  treatmentChannelAddress: '789 Hospital Drive, Phoenix, AZ 85008',
  treatmentChannelValue: '(123) 456-7890',
  treatmentChannelPortalRequestId: 'PRT-2023-789012',
  transmissionDate: new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }),
  transmissionMethod: 'Via Certified Mail',

  // Recipient Information (from correspondence package)
  recipientName: 'State Farm Insurance',
  recipientAddress1: '1 State Farm Plaza',
  recipientAddress2: 'Suite 100',
  recipientCity: 'Bloomington',
  recipientState: 'IL',
  recipientZip: '61710',
  recipientFullAddress:
    'State Farm Insurance\n1 State Farm Plaza\nSuite 100\nBloomington, IL 61710',

  // FOIA Details (nested object - CA sample with rendered authorization)
  foia: {
    state_code: 'CA',
    state_name: 'California',
    act: {
      name_short: 'CPRA',
      name_long: 'California Public Records Act',
      citation: 'Cal. Gov. Code \u00a7 6250 et seq.',
    },
    response_deadline: {
      days: 10,
      type: 'calendar_days',
      statute: 'Gov. Code \u00a7 6253(c)',
      extension_permitted: true,
      max_extension_days: 14,
    },
    deadline_text: 'ten (10) calendar days',
    authorization: {
      required: true,
      type: 'penalty_of_perjury',
      statute: 'Vehicle Code \u00a7 20012',
      language:
        'I declare under penalty of perjury under the laws of California that Johnson & Associates represents a party involved in this traffic collision',
    },
    exemptions: {
      statute: 'Gov. Code \u00a7 6254',
      catch_all_statute: 'Gov. Code \u00a7 6255',
      must_cite_specific: true,
    },
    fees: {
      per_page: 0.1,
      crash_report: 10.0,
      statute: 'Gov. Code \u00a7 6253.9',
    },
  },
};
